import React, { useState, useRef, useEffect } from 'react';
import { css } from 'emotion';
import { CaretLeft, CaretRight, Files } from 'phosphor-react';
import { Colors } from '@be-brite/briteui';
import { posthogAttrs } from '../../posthog-constants';
import { useOutsideClick } from '../use-outside-click';

const MobileFooter = ({
  show = true,
  pages = [],
  currentPageIndex = 0,
  setCurrentPageIndex,
  onPrevPage,
  onNextPage,
  disablePrevPage,
  disableNextPage,
  designStyles = {},
  pageListVisible,
  onPageListVisibleChange,
}) => {
  const navRef = useRef(null);
  const pagesRef = useOutsideClick(() => onPageListVisibleChange(false), 'MobileFooter');
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    if (navRef.current) {
      const navStyles = getComputedStyle(navRef.current);
      setFooterHeight(navRef.current.offsetHeight + parseFloat(navStyles.bottom));
    }
  }, []);

  // Focus trap for accessibility
  useEffect(() => {
    if (!pageListVisible) return;
    const focusable = pagesRef.current?.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
    if (focusable && focusable.length) {
      focusable[0].focus();
    }
  }, [pageListVisible]);

  return (
    <>
      <nav
        ref={navRef}
        aria-label="Page navigation"
        className={css`
          position: fixed;
          left: 0;
          right: 0;
          bottom: 16px;
          z-index: 1500;
          transition: transform 0.3s ease;
          transform: translateY(${show ? '0%' : '130%'});
          will-change: transform;
          padding: 0 0 8px 0;
        `}
      >
        <div
          className={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 8px 0 0 0;
          `}
        >
          <button
            tabIndex={0}
            aria-label="Go to previous page"
            onClick={onPrevPage}
            data-ph-capture-attribute-source={posthogAttrs.types.NAVIGATION}
            data-ph-capture-attribute-action={posthogAttrs.actions.PREV_PAGE}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onPrevPage();
            }}
            disabled={disablePrevPage()}
            className={css`
              background: ${disablePrevPage()
                ? `${designStyles?.Theme?.Body?.ButtonColor || '#25282D'}90`
                : designStyles?.Theme?.Body?.ButtonColor
                ? designStyles?.Theme?.Body?.ButtonColor
                : '#25282D'};
              color: #fff;
              border: none;
              border-radius: 20px 0 0 20px;
              min-height: 48px;
              min-width: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 20px;
              outline: none;
              cursor: ${disablePrevPage() ? 'not-allowed' : 'pointer'};
            `}
          >
            <CaretLeft size={24} color="#fff" />
          </button>
          <button
            tabIndex={0}
            aria-label="Open page list"
            onClick={() => onPageListVisibleChange(true)}
            data-ph-capture-attribute-source={posthogAttrs.types.NAVIGATION}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onPageListVisibleChange(true);
            }}
            className={css`
              background: ${designStyles?.Theme?.Body?.ButtonColor
                ? designStyles?.Theme?.Body?.ButtonColor
                : '#25282D'};
              color: #fff;
              border: none;
              min-height: 48px;
              min-width: 48px;
              font-size: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              outline: none;
              cursor: pointer;
              box-shadow: 0 1px 2px rgba(37, 40, 45, 0.04);
              transition: background 0.2s;
            `}
          >
            <Files size={28} weight="bold" color="#fff" />
          </button>
          <button
            tabIndex={0}
            aria-label="Go to next page"
            onClick={onNextPage}
            data-ph-capture-attribute-source={posthogAttrs.types.NAVIGATION}
            data-ph-capture-attribute-action={posthogAttrs.actions.NEXT_PAGE}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onNextPage();
            }}
            styles="icon sm"
            disabled={disableNextPage()}
            className={css`
              background: ${disableNextPage()
                ? '#D1DAE3'
                : designStyles?.Theme?.Body?.ButtonColor
                ? designStyles?.Theme?.Body?.ButtonColor
                : '#25282D'};
              color: #fff;
              border: none;
              border-radius: 0 20px 20px 0;
              min-height: 48px;
              min-width: 48px;
              outline: none;
              font-size: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              outline: none;
              cursor: pointer;
              cursor: ${disableNextPage() ? 'not-allowed' : 'pointer'};
            `}
          >
            <CaretRight size={24} color="#fff" />
          </button>
        </div>
      </nav>
      {pageListVisible && (
        <div
          className={css`
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            top: 0;
            z-index: 1400;
            display: flex;
            align-items: flex-end;
            justify-content: center;
          `}
          aria-modal="true"
          role="dialog"
        >
          <div
            ref={pagesRef}
            className={css`
              background: #fff;
              width: 100%;
              max-width: 300px;
              border-radius: 16px;
              min-height: 120px;
              max-height: 60vh;
              margin-bottom: calc(${footerHeight}px + env(safe-area-inset-bottom, 0px));
              box-shadow: 0 -2px 16px rgba(37, 40, 45, 0.18);
              overflow-y: auto;
              padding: 16px 0 8px 0;
              display: flex;
              flex-direction: column;
              align-items: stretch;
              animation: slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1);
              @keyframes slideUp {
                from {
                  transform: translateY(100%);
                }
                to {
                  transform: translateY(0%);
                }
              }
            `}
          >
            <div
              className={css`
                overflow-y: auto;
                max-height: 48vh;
                padding: 0 8px;
                display: flex;
                flex-direction: column;
                gap: 2px;
              `}
            >
              {pages.map((page, idx) => (
                <button
                  key={page.ID || idx}
                  tabIndex={0}
                  aria-label={`Go to page ${idx + 1}: ${page.Title || ''}`}
                  data-ph-capture-attribute-source={posthogAttrs.types.NAVIGATION}
                  data-ph-capture-attribute-action={posthogAttrs.actions.PREV_PAGE}
                  onClick={() => {
                    setCurrentPageIndex(idx);
                    onPageListVisibleChange(false);
                  }}
                  className={css`
                    background: ${idx === currentPageIndex ? Colors.Gray200 : 'transparent'};
                    color: '#25282D';
                    border: none;
                    border-radius: 8px;
                    padding: 14px 16px;
                    margin: 0 0 2px 0;
                    text-align: left;
                    font-size: 14px;
                    font-weight: ${idx === currentPageIndex ? 700 : 400};
                    cursor: pointer;
                    outline: none;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    transition: background 0.2s;
                    &:active {
                      background: ${designStyles?.Theme?.Body?.ButtonColor || '#25282D'}90;
                      color: #fff;
                    }
                  `}
                >
                  <span
                    className={css`
                      width: 24px;
                      height: 24px;
                      border-radius: 50%;
                      background: ${idx === currentPageIndex ? '#fff2' : '#E8EDF3'};
                      color: '#66737F';
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 14px;
                      font-weight: 600;
                    `}
                  >
                    {idx + 1}
                  </span>
                  {page.Name || `Page ${idx + 1}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileFooter;
